import { useRef, useState } from "react";
import { Clock } from "lucide-react";
import { toast } from "sonner";
import { errMsg } from "../../lib/api";
import { kycApi } from "../../lib/kycApi";
import { KycShell, BigButton, StepTitle } from "./KycShell";
import { StepConsent } from "./StepConsent";
import { StepDatos } from "./StepDatos";
import { StepDocumento } from "./StepDocumento";
import { StepConfirmar } from "./StepConfirmar";
import { StepDidit } from "./StepDidit";
import { StepManual } from "./StepManual";
import { StepEnviar, StepFin } from "./StepFin";

const Expired = ({ brand, onRestart }) => (
  <div className="text-center space-y-5 py-8" data-testid="kyc-expired">
    <Clock size={56} className="mx-auto text-slate-400" />
    <StepTitle paso="" title="Tu sesión venció">Por seguridad, cada verificación dura 30 minutos. Puedes comenzar de nuevo.</StepTitle>
    {onRestart && <BigButton color={brand.color} onClick={onRestart} data-testid="kyc-restart-button">Comenzar de nuevo</BigButton>}
  </div>
);

export const KycFlow = ({ brand, session, mode = "remoto", operador, onRestart, retorno }) => {
  const didit = session.proveedor === "didit";
  const [, setBio] = useState(retorno ? true : null);
  const [step, setStep] = useState(retorno ? "didit" : "consentimiento");
  const [busy, setBusy] = useState(false);
  const [expired, setExpired] = useState(false);
  const [fin, setFin] = useState(null);
  const [telVerif, setTelVerif] = useState(session.telefono_verificado || null);
  const [leidos, setLeidos] = useState(null);
  const t0 = useRef(Date.now());
  const presencial = mode === "presencial";
  const go = (s) => {
    t0.current = Date.now();
    setStep(s);
    window.scrollTo(0, 0);
  };
  const dur = () => Math.round((Date.now() - t0.current) / 100) / 10;

  const call = async (fn) => {
    setBusy(true);
    try {
      return await fn();
    } catch (e) {
      if (e?.response?.status === 410) setExpired(true);
      else toast.error(errMsg(e));
      return null;
    } finally {
      setBusy(false);
    }
  };

  const upload = async (tipo, blob, meta) => !!(await call(() => kycApi.upload(session, tipo, blob, { ...meta, duracion_s: dur() })));
  const leer = async () => (await call(() => kycApi.leer(session)))?.data;
  const manual = async (body) => (await call(() => kycApi.manual(session, { ...body, duracion_s: dur() })))?.data;

  const onConsent = async (body) => {
    const r = await call(() => kycApi.consent(session, { ...body, duracion_s: dur() }));
    if (r) {
      setBio(r.data.biometria);
      go(didit && r.data.biometria ? "didit" : "documento");
    }
  };
  const onLeido = (d, tel) => {
    setLeidos(d);
    if (tel) setTelVerif(tel);
    go("confirmar");
  };
  const onConfirmar = async (body) => (await call(() => kycApi.confirmarDatos(session, { ...body, duracion_s: dur() }))) && go("datos");
  const finalizar = async (confirmo) => {
    const fn = presencial ? () => kycApi.confirmarPresencial(session, { confirmo, duracion_s: dur() }) : () => kycApi.finalizar(session, { duracion_s: dur() });
    const r = await call(fn);
    if (r) setFin(r.data);
  };
  const onDatos = async (body) => {
    if (!(await call(() => kycApi.datos(session, { ...body, duracion_s: dur() })))) return;
    if (presencial) go("enviar");
    else await finalizar(false);
  };

  const ETAPA = { consentimiento: 1, didit: retorno ? 4 : 2, manual: 3, documento: 3, confirmar: 5, datos: 5, enviar: 5 };
  const etapa = expired ? null : fin ? 6 : ETAPA[step];
  const paso = null;

  return (
    <KycShell brand={brand} etapa={etapa} pantalla={fin ? "fin" : expired ? "expirada" : step}>
      {expired ? <Expired brand={brand} onRestart={onRestart} />
        : fin ? <StepFin brand={brand} videollamada={fin.videollamada} returnUrl={session.return_url} />
        : step === "consentimiento" ? <StepConsent brand={brand} onSubmit={onConsent} busy={busy} didit={didit} />
        : step === "didit" ? <StepDidit brand={brand} session={session} call={call} retorno={retorno} paso={paso} onDone={onLeido} onManual={() => go("manual")} />
        : step === "manual" ? <StepManual brand={brand} busy={busy} onSubmit={async (b) => { const r = await manual(b); if (r?.ok) onLeido(r.datos); }} />
        : step === "documento" ? <StepDocumento brand={brand} upload={upload} leer={leer} manual={manual} paso={paso} onDone={onLeido} />
        : step === "confirmar" ? <StepConfirmar brand={brand} datos={leidos} onSubmit={onConfirmar} busy={busy} paso={paso} />
        : step === "datos" ? <StepDatos brand={brand} onSubmit={onDatos} busy={busy} nacionalidad={leidos?.nacionalidad} paso={paso} prefill={session.prefill} telVerificado={telVerif} />
        : <StepEnviar brand={brand} mode={mode} operador={operador} onSubmit={finalizar} busy={busy} />}
    </KycShell>
  );
};
